import {
  QUALITY_CONTROL_SECTION_TITLES,
  QualityControlFlag,
  QualityControlOutput,
  QualityControlSection,
  getNumberedPendingQualityControlSections,
  getQualityControlFlagTitle,
} from '@aloft/toolkit-shared';
import { Block, Flex, Typography } from '@aloft/toolkit-sui';

import { MutableRefObject, RefObject } from 'react';

import { QualityControlReviewForm } from './QualityControlReviewForm.tsx';
import { useResolveQualityControlFlag } from './useResolveQualityControlFlag.ts';

const photoUrlsFromFlag = (flag: QualityControlFlag): string[] =>
  'photoUrls' in flag ? flag.photoUrls : [];

const QualityControlReviewFlag = ({ flag, index }: { flag: QualityControlFlag; index: number }) => {
  const title = getQualityControlFlagTitle(flag.category);
  const photoUrls = photoUrlsFromFlag(flag);

  return (
    <Flex flexDirection="column" gap="12px" width="100%">
      <Typography variant="h4" color="buttonDefault">
        {`${index}. ${title}`}
      </Typography>
      <Flex flexDirection="column" gap="12px" paddingLeft="20px" width="100%">
        <Typography variant="body1">{flag.description}</Typography>
        {photoUrls.length > 0 && (
          <Flex gap="12px" flexWrap="wrap">
            {photoUrls.map((url, photoIndex) => (
              <Flex
                key={`${url}-${photoIndex}`}
                width="120px"
                height="120px"
                overflow="hidden"
                flexShrink={0}
              >
                <img
                  src={url}
                  alt={title}
                  width={120}
                  height={120}
                  style={{ objectFit: 'cover', display: 'block' }}
                />
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

const CENTER_COLUMN_MAX_WIDTH = '720px';
const FORM_COLUMN_WIDTH = '560px';

export const QualityControlReviewDetails = ({
  qualityControl,
  scrollContainerRef,
  sectionRefs,
  updateQualityControl,
}: {
  qualityControl: QualityControlOutput;
  scrollContainerRef: RefObject<HTMLDivElement>;
  sectionRefs: MutableRefObject<Partial<Record<QualityControlSection, HTMLDivElement | null>>>;
  updateQualityControl: (body: QualityControlOutput) => Promise<QualityControlOutput>;
}) => {
  const numberedSections = getNumberedPendingQualityControlSections(qualityControl);
  const { resolveFlag } = useResolveQualityControlFlag(qualityControl, updateQualityControl);

  return (
    <Flex
      ref={scrollContainerRef}
      data-testid="quality-control-review-scroll-container"
      flex={1}
      minWidth={0}
      minHeight={0}
      overflow="auto"
      backgroundColor="surface6"
      __dangerDoNotUseSx={{
        scrollBehavior: 'smooth',
        scrollSnapType: 'y proximity',
      }}
    >
      <Flex flexDirection="column" flex={1} minWidth={0} padding="24px" gap="40px">
        {numberedSections.map(({ section, flags }) => (
          <Flex
            key={section}
            ref={(element) => {
              sectionRefs.current ||= {};
              sectionRefs.current[section] = element;
            }}
            flexDirection="column"
            gap="24px"
            width="100%"
            __dangerDoNotUseSx={{ scrollSnapAlign: 'start' }}
          >
            <Flex alignItems="flex-start" gap="0" width="100%">
              <Flex flex={1} minWidth={0} maxWidth={CENTER_COLUMN_MAX_WIDTH}>
                <Typography variant="h1">{QUALITY_CONTROL_SECTION_TITLES[section]}</Typography>
              </Flex>
              <Block width={FORM_COLUMN_WIDTH} flexShrink={0} />
            </Flex>
            {flags.map(({ flag, index }, flagIndex) => {
              const firstPendingFlagIndex = flags.findIndex(
                ({ flag: sectionFlag }) => sectionFlag.status === 'pending',
              );

              return (
                <Flex key={`${section}-${flag.category}-${index}`} alignItems="flex-start" gap="0">
                  <Flex
                    flex={1}
                    minWidth={0}
                    maxWidth={CENTER_COLUMN_MAX_WIDTH}
                    paddingRight="24px"
                  >
                    <QualityControlReviewFlag flag={flag} index={index} />
                  </Flex>
                  <Flex width={FORM_COLUMN_WIDTH} paddingX="24px" flexShrink={0}>
                    <Flex width="100%">
                      <QualityControlReviewForm
                        flag={flag}
                        index={index}
                        defaultExpanded={
                          flag.status === 'pending' && flagIndex === firstPendingFlagIndex
                        }
                        onResolveFlag={(pendingFlag) => {
                          void resolveFlag(pendingFlag);
                        }}
                      />
                    </Flex>
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        ))}
        <Block height="96px" flexShrink={0} />
      </Flex>
    </Flex>
  );
};
